import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getCarBrand,
  getCarModels,
  getCarModelYear,
  getCarColor,
  getCarFuelType,
  getCarCylinder,
} from '../../Redux/Actions/UserAction';

/**
 * [CF 2026-08-02] Car filter panel — the same nine fields the iOS app offers on
 * its car screens (`CarDealerSaleFilterViewController`): brand, model, year,
 * budget, kilometres, exterior + interior colour, fuel type, cylinders.
 *
 * Rendered as a SIBLING row under the listing header, never inside
 * `.filter-row` — dropping controls into that row is what pushed the whole page
 * sideways on Spare Parts (see the `.cav-filter-panel` note in custom.css).
 */

export const EMPTY_CAR_FILTER = {
  brandId: '',
  carModel: '',
  /* [CF 2026-08-09 #8] The model dropdown is built from the carModels master
     and its option VALUE is the English `name`, but a car's `carModel` column
     is free text the owner typed — for most of the real stock that is the
     ARABIC name ("لاند كروزر", not "Land Cruiser"). Matching on one spelling
     found nothing, which is why Car Dealers "did not return all dealers that
     have the selected model". Carry the master's Arabic name alongside so
     both the client-side matcher and the API can accept either. */
  carModelAr: '',
  modelYearId: '',
  fuelTypeId: '',
  cyclinderId: '',
  exteriorColorId: '',
  interiorColorId: '',
  priceMin: '',
  priceMax: '',
  kmMin: '',
  kmMax: '',
};

export function isCarFilterActive(f) {
  return !!f && Object.keys(EMPTY_CAR_FILTER).some((k) => String(f[k] ?? '') !== '');
}

const numOr = (v, fallback) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : fallback;
};

/** Client-side match — the cars-for-sale listing already carries every field. */
export function carMatchesFilter(car, f) {
  if (!isCarFilterActive(f)) return true;
  if (f.brandId && car?.brand?.brandId !== f.brandId) return false;
  if (f.carModel) {
    const actual = String(car?.carModel || '').trim().toLowerCase();
    const wanted = [f.carModel, f.carModelAr]
      .filter(Boolean)
      .map((v) => String(v).trim().toLowerCase());
    if (!wanted.includes(actual)) return false;
  }
  if (f.modelYearId && car?.modelYear?.modelYearId !== f.modelYearId) return false;
  if (f.fuelTypeId && car?.fuelType?.fuelTypeId !== f.fuelTypeId) return false;
  if (f.cyclinderId && car?.cyclinder?.cyclinderId !== f.cyclinderId) return false;
  if (f.exteriorColorId && car?.exteriorColor?.colorId !== f.exteriorColorId) return false;
  if (f.interiorColorId && car?.interiorColor?.colorId !== f.interiorColorId) return false;

  const price = numOr(car?.price, null);
  if (f.priceMin !== '' && (price === null || price < numOr(f.priceMin, -Infinity))) return false;
  if (f.priceMax !== '' && (price === null || price > numOr(f.priceMax, Infinity))) return false;

  const km = numOr(car?.kilometerDriven, null);
  if (f.kmMin !== '' && (km === null || km < numOr(f.kmMin, -Infinity))) return false;
  if (f.kmMax !== '' && (km === null || km > numOr(f.kmMax, Infinity))) return false;

  return true;
}

const fieldStyle = {
  width: '100%',
  minWidth: 0,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  padding: '9px 12px',
  border: '1px solid #c9ced4',
  borderRadius: '10px',
  background: '#fff',
  font: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '.72rem',
  letterSpacing: '.03em',
  textTransform: 'uppercase',
  color: '#8a9099',
  marginBottom: '4px',
  fontWeight: 600,
};

/* [CF 2026-08-02] MODULE level, deliberately. Defined inside the component
   these would be a new type on every render, so React remounts each field —
   which steals focus after every keystroke in the price / kilometre inputs. */
function FieldSelect({ label, items, idKey, en, ar, placeholder, selected, onSelect, L }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      <select value={selected} onChange={onSelect} style={fieldStyle}>
        <option value="">{placeholder}</option>
        {list.map((o) => (
          <option key={o[idKey]} value={o[idKey]}>
            {L(o[en], o[ar])}
          </option>
        ))}
      </select>
    </label>
  );
}

function FieldRange({ label, unit, minValue, maxValue, onMin, onMax, minLabel, maxLabel }) {
  return (
    <div style={{ minWidth: 0 }}>
      <span style={labelStyle}>
        {label}
        {unit ? ` (${unit})` : ''}
      </span>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input type="number" min="0" inputMode="numeric" placeholder={minLabel}
          value={minValue} onChange={onMin} style={fieldStyle} />
        <input type="number" min="0" inputMode="numeric" placeholder={maxLabel}
          value={maxValue} onChange={onMax} style={fieldStyle} />
      </div>
    </div>
  );
}

export default function CarFilterPanel({
  value,
  onChange,
  onApply,
  hide = [],
  narrowToRows = null,
}) {
  const shows = (k) => !hide.includes(k);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  const L = (en, ar) => (isRTL ? ar || en : en || ar);
  const home = useSelector((state) => state?.home);

  const brands = home?.garageCarBrandList;
  const models = home?.carModels;
  const years = home?.CarModelYears;
  const colors = home?.CarColors;
  const fuels = home?.CarFuelTypes;
  const cylinders = home?.CarCylinders;

  useEffect(() => {
    dispatch(getCarBrand());
    dispatch(getCarModelYear());
    dispatch(getCarColor());
    dispatch(getCarFuelType());
    dispatch(getCarCylinder());
  }, [dispatch]);

  // Models depend on the chosen brand — same endpoint the add-car forms use.
  useEffect(() => {
    if (value.brandId) dispatch(getCarModels(value.brandId));
  }, [dispatch, value.brandId]);

  const set = (key) => (e) => {
    const next = { ...value, [key]: e.target.value };
    // A new brand invalidates whichever model was picked under the old one.
    if (key === 'brandId') {
      next.carModel = '';
      next.carModelAr = '';
    }
    onChange(next);
  };

  const asArray = (v) => (Array.isArray(v) ? v : []);

  /* [CF 2026-08-17 V2] FACETED narrowing — every dropdown offers only the values
     that still exist in the listing once every OTHER active filter is applied.
     Pick Toyota and the colour list drops to the colours Toyotas are actually
     listed in, so no combination the user can assemble lands on an empty page.
     The masters are big (97 brands, 46 years) while the listing itself holds far
     fewer — 10 brands, 5 years, 6 colours at the time of writing — so without
     this most choices are dead ends.

     Each facet is computed with its OWN value cleared. Without that, choosing a
     colour would collapse the colour list to just that colour and there would be
     no way to switch to another one.

     Only pages holding the complete unfiltered rows can do this. Car Dealers and
     Rent a Car re-query the server per filter AND their rows are businesses, not
     cars — they carry no brand/year/colour to count — so they pass nothing here
     and keep the full masters. */
  const facets = useMemo(() => {
    const rows = Array.isArray(narrowToRows) ? narrowToRows : null;
    if (!rows || !rows.length) return null;

    const pick = {
      brandId: (c) => c?.brand?.brandId,
      modelYearId: (c) => c?.modelYear?.modelYearId,
      fuelTypeId: (c) => c?.fuelType?.fuelTypeId,
      cyclinderId: (c) => c?.cyclinder?.cyclinderId,
      exteriorColorId: (c) => c?.exteriorColor?.colorId,
      interiorColorId: (c) => c?.interiorColor?.colorId,
      carModel: (c) => String(c?.carModel || '').trim().toLowerCase(),
    };

    const collect = (list, key) => {
      const s = new Set();
      list.forEach((c) => {
        const v = pick[key](c);
        if (v) s.add(v);
      });
      return s;
    };

    /* Values present anywhere in the listing. Used as the floor when the other
       filters between them already match nothing — falling back to the whole
       master there would put the 97 brands back on screen. */
    const anywhere = {};
    Object.keys(pick).forEach((k) => {
      anywhere[k] = collect(rows, k);
    });

    const out = {};
    Object.keys(pick).forEach((k) => {
      const probe = { ...value, [k]: '' };
      // Brand and model are one choice: clearing the brand must clear the model
      // it was picked under, or the model keeps constraining the brand list.
      if (k === 'brandId') {
        probe.carModel = '';
        probe.carModelAr = '';
      }
      if (k === 'carModel') probe.carModelAr = '';
      const subset = rows.filter((c) => carMatchesFilter(c, probe));
      const found = collect(subset, k);
      out[k] = found.size ? found : anywhere[k];
    });
    return out;
  }, [narrowToRows, value]);

  const narrow = (items, idKey, facetKey) => {
    const list = asArray(items);
    const allowed = facets && facets[facetKey];
    if (!allowed || !allowed.size) return list;
    return list.filter((o) => allowed.has(o[idKey]));
  };

  /* Models are matched on the free-text name the owner typed, which may be the
     English or the Arabic spelling — so accept either. */
  const narrowModels = (items) => {
    const list = asArray(items);
    const allowed = facets && facets.carModel;
    if (!allowed || !allowed.size) return list;
    const has = (v) => !!v && allowed.has(String(v).trim().toLowerCase());
    return list.filter((m) => has(m.name) || has(m.nameAr));
  };

  /* [CF 2026-08-17 V2/V3/V8] There used to be `Select` and `Range` wrapper
     components declared RIGHT HERE, inside the render. That silently undid the
     module-level fix above: a component declared in the body is a brand-new
     type on every render, so React unmounted and remounted the whole subtree —
     including the <input> — after each keystroke, and the phone keyboard closed
     on every digit typed into Price / Kilometres. The module-level components
     are now used directly at each call site; nothing below may re-wrap them. */

  /* [CF 2026-08-17 #11.18] The year master arrives in insertion order, which put
     the dropdown in a random-looking sequence (2011, 1984, 2019, 1981...).
     Client asked for newest first. */
  const byYearDesc = (list) =>
    [...list].sort(
      (a, b) => (parseInt(b?.modelYear, 10) || 0) - (parseInt(a?.modelYear, 10) || 0)
    );

  return (
    <div className="cav-filter-panel cav-car-filter" style={{ textAlign: isRTL ? 'right' : 'left' }}>
      <div className="cav-car-filter-grid">
        <FieldSelect
          label={t('Car Brand')}
          items={narrow(brands, 'brandId', 'brandId')}
          idKey="brandId"
          en="name"
          ar="nameAr"
          placeholder={t('All Brands')}
          selected={value.brandId}
          onSelect={set('brandId')}
          L={L}
        />

        <label style={{ display: 'block', minWidth: 0 }}>
          <span style={labelStyle}>{t('Car Model')}</span>
          <select
            value={value.carModel}
            onChange={(e) => {
              const picked = asArray(models).find((m) => m.name === e.target.value);
              onChange({
                ...value,
                carModel: e.target.value,
                carModelAr: picked?.nameAr || '',
              });
            }}
            style={fieldStyle}
            disabled={!value.brandId}
          >
            <option value="">
              {value.brandId ? t('All Models') : t('Select a brand first')}
            </option>
            {narrowModels(models).map((m) => (
              <option key={m.carModelId || m.name} value={m.name}>
                {L(m.name, m.nameAr)}
              </option>
            ))}
          </select>
        </label>

        <FieldSelect
          label={t('Year')}
          items={byYearDesc(narrow(years, 'modelYearId', 'modelYearId'))}
          idKey="modelYearId"
          en="modelYear"
          ar="modelYearAr"
          placeholder={t('Any Year')}
          selected={value.modelYearId}
          onSelect={set('modelYearId')}
          L={L}
        />
        {shows('fuelTypeId') && (
        <FieldSelect
          label={t('Fuel Type')}
          items={narrow(fuels, 'fuelTypeId', 'fuelTypeId')}
          idKey="fuelTypeId"
          en="fuelType"
          ar="fuelTypeAr"
          placeholder={t('Any Fuel Type')}
          selected={value.fuelTypeId}
          onSelect={set('fuelTypeId')}
          L={L}
        />
        )}
        {shows('cyclinderId') && (
        <FieldSelect
          label={t('Cylinder')}
          items={narrow(cylinders, 'cyclinderId', 'cyclinderId')}
          idKey="cyclinderId"
          en="cyclinder"
          ar="cyclinderAr"
          placeholder={t('Any Cylinder')}
          selected={value.cyclinderId}
          onSelect={set('cyclinderId')}
          L={L}
        />
        )}
        {shows('exteriorColorId') && (
        <FieldSelect
          label={t('Exterior Color')}
          items={narrow(colors, 'colorId', 'exteriorColorId')}
          idKey="colorId"
          en="color"
          ar="colorAr"
          placeholder={t('Any Color')}
          selected={value.exteriorColorId}
          onSelect={set('exteriorColorId')}
          L={L}
        />
        )}
        {shows('interiorColorId') && (
        <FieldSelect
          label={t('Interior Color')}
          items={narrow(colors, 'colorId', 'interiorColorId')}
          idKey="colorId"
          en="color"
          ar="colorAr"
          placeholder={t('Any Color')}
          selected={value.interiorColorId}
          onSelect={set('interiorColorId')}
          L={L}
        />
        )}
        <FieldRange
          label={t('Price')}
          unit="KD"
          minValue={value.priceMin}
          maxValue={value.priceMax}
          onMin={set('priceMin')}
          onMax={set('priceMax')}
          minLabel={t('Min')}
          maxLabel={t('Max')}
        />
        {/* Rent a Car hides this: rental cars carry no odometer reading. */}
        {shows('kmMin') && (
          <FieldRange
            label={t('Kilometers')}
            unit="KM"
            minValue={value.kmMin}
            maxValue={value.kmMax}
            onMin={set('kmMin')}
            onMax={set('kmMax')}
            minLabel={t('Min')}
            maxLabel={t('Max')}
          />
        )}
      </div>

      {/* [CF 2026-08-02] Results update as you choose, so Apply is really
          "done" — it closes the panel and hands the screen back to the list.
          Reset clears every field but leaves the panel open to pick again. */}
      <div className="cav-car-filter-actions">
        <button
          type="button"
          className="cav-filter-reset"
          onClick={() => onChange({ ...EMPTY_CAR_FILTER })}
          disabled={!isCarFilterActive(value)}
        >
          {t('Reset')}
        </button>
        <button type="button" className="cav-filter-apply" onClick={onApply}>
          {t('Apply')}
        </button>
      </div>
    </div>
  );
}
